import { Layout, Users, Activity, Target, Gauge, Zap, TrendingUp } from 'lucide-react';
import KeywordLandingPage, {
  type ProblemCard,
  type FeatureCard,
} from '../components/KeywordLandingPage';

const problemCards: ProblemCard[] = [
  {
    title: 'Decisions lose their context',
    description:
      'Market observations, company strengths, and long-term ambitions are difficult to weigh when they are spread across files.',
    icon: Layout,
    iconBgClass: 'bg-red-100',
    iconColorClass: 'text-red-600',
  },
  {
    title: 'Priorities compete for attention',
    description:
      'Teams can pursue useful projects without a clear view of the objectives those projects are meant to support.',
    icon: Users,
    iconBgClass: 'bg-orange-100',
    iconColorClass: 'text-orange-600',
  },
  {
    title: 'The blank page slows the discussion',
    description:
      'Turning company knowledge into a coherent draft takes effort before you can review the strategic choices.',
    icon: Activity,
    iconBgClass: 'bg-yellow-100',
    iconColorClass: 'text-yellow-600',
  },
];

const features: FeatureCard[] = [
  {
    title: 'Company direction',
    description:
      'Give your strategy a clear starting point with vision, mission, values, and objectives.',
    icon: Target,
    gradientFrom: 'from-blue-50',
    gradientTo: 'to-blue-100',
    iconColorClass: 'text-blue-600',
  },
  {
    title: 'Organisation and ownership',
    description: 'Map divisions, teams, and positions so responsibility has a place in the plan.',
    icon: Users,
    gradientFrom: 'from-purple-50',
    gradientTo: 'to-purple-100',
    iconColorClass: 'text-purple-600',
  },
  {
    title: 'Connected delivery',
    description: 'Carry strategic choices into projects, tasks, and subtasks in the Execution Hub.',
    icon: Activity,
    gradientFrom: 'from-green-50',
    gradientTo: 'to-green-100',
    iconColorClass: 'text-green-600',
  },
  {
    title: 'Business assessments',
    description: 'Capture PESTEL, Five Forces, SWOT, success factors, and competitor profiles.',
    icon: Gauge,
    gradientFrom: 'from-orange-50',
    gradientTo: 'to-orange-100',
    iconColorClass: 'text-orange-600',
  },
  {
    title: 'AI drafting and recommendations',
    description:
      'Explore starting points for objectives and strategies based on the context you supply.',
    icon: Zap,
    gradientFrom: 'from-red-50',
    gradientTo: 'to-red-100',
    iconColorClass: 'text-red-600',
  },
  {
    title: 'A view of the whole plan',
    description: 'Follow the work hierarchy and create custom visual reports for discussion.',
    icon: TrendingUp,
    gradientFrom: 'from-indigo-50',
    gradientTo: 'to-indigo-100',
    iconColorClass: 'text-indigo-600',
  },
];

export default function BusinessStrategySoftwarePage() {
  return (
    <KeywordLandingPage
      headline="Choose your direction. Connect it to the work."
      subheadline="Build your business strategy with guided assessments and AI assistance, then carry your choices through to objectives, projects, and tasks."
      bullets={[
        'Company direction and measurable objectives',
        'External and internal assessments',
        'Strategy drafting from your context',
        'Connected projects, tasks, and reports',
      ]}
      badgeText="Business Strategy"
      problemTitle="Good strategic choices need the whole picture."
      problemSubtitle="Your direction, understanding of the business, and delivery priorities need to inform each other as you build the plan."
      problemCards={problemCards}
      solutionTitle="Work through the decisions that shape your business."
      solutionSubtitle="StratPlan brings your foundation, assessments, strategy choices, and execution into a guided five-step workflow."
      solutionSteps={[
        {
          title: 'Set the direction',
          description:
            'Define your vision, mission, values, and measurable objectives as a foundation for the choices ahead.',
        },
        {
          title: 'Assess and choose',
          description:
            'Capture external and internal factors, develop strategy options, and refine AI drafts using your business context.',
        },
        {
          title: 'Connect choices to delivery',
          description:
            'Link strategies to projects and tasks, assign owners, and present your plan in a visual report.',
        },
      ]}
      featuresTitle="Build a strategy with context and a path to action"
      features={features}
      finalCtaTitle="Build the strategy behind your next stage."
      finalCtaSubtitle="Create your account and bring your company direction, priorities, and delivery plan together."
      finalCtaButtonText="Start my strategic plan"
      analyticsCategory="business_strategy_landing"
      breadcrumbLabel="Business Strategy"
    />
  );
}
